import React from 'react';
import DOMPurify from 'dompurify';
import { parseUrlsWithLineBreaks } from '../utils/urlParser';
import './FormattedContent.css';

// Tags/attrs RichTextEditor's toolbar (headings, font, size, bold/italic/
// underline/strike, text/highlight color, blockquote, lists, link) can
// actually produce - kept in sync with RichTextEditor.js's
// `modules.toolbar` and `formats`. Widening the editor's toolbar without
// widening this allowlist means the new formatting gets silently stripped
// when the note is displayed back - keep the two in sync.
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'ol', 'ul', 'li', 'a', 'blockquote', 'span'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'class'];

// Quill's background (highlight) format sets inline style=
// "background-color: ..." on <span> elements. DOMPurify's ALLOWED_ATTR
// permits the `style` attribute as a whole but (by default) doesn't parse
// its contents, so as defense-in-depth this hook throws away anything in a
// style attribute that isn't exactly that one declaration with a hex/rgb(a)
// value - the only shape Quill's picker ever generates - before the
// sanitized HTML is used. Runs on every DOMPurify.sanitize() call in the
// app; harmless for other content since it only touches elements that
// already have a style attribute (nothing else here sets one).
//
// Deliberately excludes `color`: the editor's text-color picker let coaches
// write notes in colors that could end up unreadable (or wildly
// inconsistent from note to note) depending on the viewer's theme, so
// text color is normalized to the theme's own default everywhere instead
// of trusting whatever was authored - this strips it from already-stored
// notes too, not just new ones, since sanitization runs at render time.
const SAFE_STYLE_DECL = /(?:^|;)\s*(background-color)\s*:\s*(#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,]+\))\s*(?=;|$)/g;

// Quill's font/size formats instead set a CLASS on <span> elements
// (ql-font-serif, ql-size-large, etc) rather than an inline style, so they
// need their own allowlist - these are the only values Quill's default
// (unmodified) font/size whitelists can ever produce. FormattedContent.css
// mirrors quill.snow.css's font-family/font-size rules for these exact
// classes so notes render identically outside the editor.
const SAFE_CLASSES = new Set([
  'ql-font-serif', 'ql-font-monospace',
  'ql-size-small', 'ql-size-large', 'ql-size-huge',
]);

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.hasAttribute && node.hasAttribute('style')) {
    const matches = node.getAttribute('style').match(SAFE_STYLE_DECL);
    if (matches && matches.length > 0) {
      node.setAttribute('style', matches.map(m => m.replace(/^;/, '').trim()).join('; '));
    } else {
      node.removeAttribute('style');
    }
  }

  if (node.hasAttribute && node.hasAttribute('class')) {
    const safe = node.getAttribute('class').split(/\s+/).filter(c => SAFE_CLASSES.has(c));
    if (safe.length > 0) {
      node.setAttribute('class', safe.join(' '));
    } else {
      node.removeAttribute('class');
    }
  }
});

// Quill always wraps content in block tags (<p>, <ul>, etc), so any string
// that came out of RichTextEditor will contain a real tag. Legacy notes
// written before rich text existed are plain text and contain none.
const looksLikeHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

/**
 * FormattedContent - renders note content written by the rich text editor
 * (sanitized HTML) while staying backward-compatible with older notes that
 * are just plain text (auto-linkifies URLs and preserves line breaks, same
 * as before rich text existed).
 *
 * @param {string} content - HTML (from RichTextEditor) or legacy plain text
 * @param {string} className - Optional CSS class
 */
const FormattedContent = ({ content, className = '' }) => {
  if (!content) {
    return <div className={`formatted-content empty ${className}`}>No content</div>;
  }

  if (!looksLikeHtml(content)) {
    return (
      <div className={`formatted-content plain ${className}`}>
        {parseUrlsWithLineBreaks(content)}
      </div>
    );
  }

  const clean = DOMPurify.sanitize(content, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });

  // Force safe defaults on any link Quill produced, same as parseUrlsInText
  // does for legacy plain-text links.
  const withSafeLinks = clean.replace(
    /<a\s+/gi,
    '<a target="_blank" rel="noopener noreferrer" '
  );

  return (
    <div
      className={`formatted-content ${className}`}
      dangerouslySetInnerHTML={{ __html: withSafeLinks }}
    />
  );
};

export default FormattedContent;
